import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { SaleStatisticFilter } from "../fragments.dispatches/SaleStatisticFilter";

const DAY_LABELS = ["LUNES", "MARTES", "MIÉRCOLES", "JUEVES", "VIERNES", "SÁBADO"];

export function SaleStatisticPage() {
    const barChartRef = useRef(null);

    const [salesWeekData, setSalesWeekData] = useState({
        monday: 0, tuesday: 0, wednesday: 0,
        thursday: 0, friday: 0, saturday: 0, sunday: 0,
    });

    useEffect(() => {
        const barChart = new Chart(barChartRef.current, {
            type: "bar",
            data: {
                labels: DAY_LABELS,
                datasets: [{
                    label: "PREVENTAS Y AUTOVENTAS",
                    data: [
                        salesWeekData.monday, salesWeekData.tuesday,
                        salesWeekData.wednesday, salesWeekData.thursday,
                        salesWeekData.friday, salesWeekData.saturday,
                    ],
                    backgroundColor: "#07bc0c",
                    borderRadius: 6,
                    borderSkipped: false,
                }],
            },
            options: {
                responsive: true,
                plugins: {
                    legend: { position: "top" },
                    tooltip: { callbacks: { label: (ctx) => ` S/ ${Number(ctx.raw).toFixed(2)}` } },
                },
                scales: {
                    y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.05)" } },
                    x: { grid: { display: false } },
                },
            },
        });
        return () => barChart.destroy();
    }, [salesWeekData]);

    // Weekly total
    const weekTotal = Object.values(salesWeekData).reduce((s, v) => s + Number(v), 0);

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Ventas por semana
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Reportes</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Estadísticas de ventas</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

                        {/* Filter panel */}
                        <div className="lg:col-span-4">
                            <SaleStatisticFilter setSalesWeekData={setSalesWeekData} />

                            {/* Weekly summary cards */}
                            {weekTotal > 0 && (
                                <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                            <i className="fas fa-chart-bar text-emerald-500"></i>
                                            Resumen semanal
                                        </h3>
                                    </div>
                                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {[
                                            { label: "Lunes",     val: salesWeekData.monday },
                                            { label: "Martes",    val: salesWeekData.tuesday },
                                            { label: "Miércoles", val: salesWeekData.wednesday },
                                            { label: "Jueves",    val: salesWeekData.thursday },
                                            { label: "Viernes",   val: salesWeekData.friday },
                                            { label: "Sábado",    val: salesWeekData.saturday },
                                        ].map(({ label, val }) => (
                                            <div key={label} className="flex items-center justify-between px-4 py-2">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
                                                <span className={`text-xs font-semibold ${Number(val) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400 dark:text-gray-500"}`}>
                                                    S/ {Number(val).toFixed(2)}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-700/50">
                                            <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Total</span>
                                            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                                S/ {weekTotal.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Chart panel */}
                        <div className="lg:col-span-8">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <i className="fas fa-chart-bar text-blue-500"></i>
                                        Preventas y autoventas por día
                                    </h2>
                                    {weekTotal > 0 && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Total semana: <span className="font-semibold text-emerald-600 dark:text-emerald-400">S/ {weekTotal.toFixed(2)}</span>
                                        </p>
                                    )}
                                </div>
                                <div className="p-5">
                                    <canvas ref={barChartRef} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
